import React from "react";
import { DialogProps } from "../../types/dialog-types";
import { getTigerResource } from "../../utils/tigerResource";
import { PickerKey, pickerInitialDir, pickerSavePath, rememberPickerDir } from "../../utils/pickerFolder";
import { closeAllToolWindows, countToolWindows } from "../../utils/closeToolWindows";
import { loadToolSet } from "../../utils/toolSetLoad";
import { saveToolSet } from "../../utils/toolSetSnapshot";
import { acrylicStyles, acrylicDialogSurface } from "../../theme/acrylic";

interface ToolSet {
  name: string;
  /** Default (read-only) sets shipped in config cannot be removed. */
  isDefault: boolean;
}

/** Shape returned by the `get_tool_set_list` command (Rust `ToolSetItem`). */
interface ToolSetItem {
  name: string;
  default_set: boolean;
}

interface ToolSetDialogProps extends DialogProps {
  toolSets?: ToolSet[];
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

// Shared visual theme with GraphChannelSelectionDialog: a frosted overlay over a
// rounded, resizable card with a header (title + ✕), a scrollable body of cards,
// and a footer carrying the Close action.
const styles = {
  ...acrylicStyles,
  // Offsets centre a 520 × 560 card; resizing grows it from this anchor
  // toward the bottom-right (see acrylicDialogSurface).
  dialog: acrylicDialogSurface({ width: 520, height: 560, minWidth: 420, minHeight: 440 }),
} as const;

export function ToolSetDialog({
  open,
  onClose,
  onApply,
  toolSets = [],
}: ToolSetDialogProps) {
  const [setName, setSetName] = React.useState<string>("");
  // Save failure message (e.g. attempting to overwrite a default set).
  const [saveError, setSaveError] = React.useState<string>("");
  const [selectedIndex, setSelectedIndex] = React.useState<number>(-1);
  const [items, setItems] = React.useState<ToolSet[]>(toolSets);
  // Number of open graph / data-grid / diagnostics tool windows. "Close All" is
  // only enabled when at least one is open (C++ `update_dialogue`).
  const [toolWindowCount, setToolWindowCount] = React.useState<number>(0);

  // Read the tool-set list from the backend (the ini files). Mirrors C++
  // `update_dialogue`, which clears the list control and re-inserts every set
  // and refreshes the open-tool-window count for the Close All button.
  const refresh = React.useCallback(async () => {
    setToolWindowCount(await countToolWindows());
    try {
      let rows: ToolSetItem[];
      if (isTauri()) {
        const { invoke } = await import("@tauri-apps/api/core");
        rows = await invoke<ToolSetItem[]>("get_tool_set_list");
      } else {
        // Browser/webserver: the list comes from /api, scoped to the login.
        const resp = await fetch("/api/get_tool_set_list");
        if (!resp.ok) return;
        rows = (await resp.json()) as ToolSetItem[];
      }
      setItems(rows.map((r) => ({ name: r.name, isDefault: r.default_set })));
      setSelectedIndex(-1);
    } catch {
      // leave the list as-is on failure
    }
  }, []);

  // Populate when the dialog opens (C++ `tool_set_shell_c::OnInitDialog`).
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      if (!cancelled) await refresh();
    })();
    return () => {
      cancelled = true;
    };
  }, [open, refresh]);

  const hasSelection = selectedIndex >= 0 && selectedIndex < items.length;
  const selectedIsDefault = hasSelection && items[selectedIndex].isDefault;
  // C++ `OnItemClickToolSetListCtrl` / `update_dialogue`: Remove is disabled for
  // default (read-only) sets, even when one is selected.
  const canRemove = hasSelection && !selectedIsDefault;
  const canCloseAll = toolWindowCount > 0;

  // Port of C++ `OnToolSetSaveButton` -> `Ui_save_tools_as_set`: snapshot every
  // open tool window into the named set in the user's tool-set ini, then
  // re-read the list. A default (read-only) set's name is refused, mirroring
  // the C++ "Can't overwrite default tool set" message.
  const handleSave = async () => {
    const name = setName.trim();
    if (!name) return;
    setSaveError("");
    // saveToolSet collects every open tool window's descriptor (Tauri snapshot
    // round-trip on desktop, BroadcastChannel on web) and persists them to the
    // login's tool-set ini. Re-read the list afterwards so the new set shows up.
    try {
      const result = await saveToolSet(name);
      if (!result.saved) {
        setSaveError(getTigerResource("TIGER_TOOLSETS_DEFAULT"));
        return;
      }
      await refresh();
    } catch (e) {
      console.error("Save tool set failed:", e);
      setSaveError(String(e));
      return;
    }
    onApply?.({ action: "save", setName: name });
    setSetName("");
  };

  // C++ `OnCloseAllWindowsButton` -> `Ui_delete_all_tool_windows` then
  // `update_dialogue`: close every graph / data-grid / diagnostics window
  // (leaving the main window), then refresh the open-window count so the button
  // disables itself.
  const handleCloseAll = async () => {
    if (!canCloseAll) return;
    try {
      await closeAllToolWindows();
    } catch (e) {
      console.error("Close All Tool Windows failed:", e);
    }
    setToolWindowCount(await countToolWindows());
    onApply?.({ action: "closeAll" });
  };

  // Port of C++ `OnToolSetLoadButton` -> `Ui_load_tool_set`: read the set's
  // saved dialogs from the ini and open a tool window for each. Refresh the
  // open-window count afterwards so Close All enables itself.
  const handleLoad = async (name: string) => {
    try {
      const result = await loadToolSet(name);
      if (result && result.skipped.length > 0) {
        console.warn("Tool set dialogs without a Tauri equivalent were skipped:", result.skipped);
      }
    } catch (e) {
      console.error("Load tool set failed:", e);
    }
    setToolWindowCount(await countToolWindows());
    onApply?.({ action: "load", setName: name });
  };

  // Port of C++ `OnToolSetRemoveButton` -> `Ui_delete_tool_set`: delete the
  // selected user set's section from the user tool-set ini, then re-read the
  // list. Default sets are read-only; the backend refuses them and we guard the
  // button, so this only fires for removable sets.
  const handleRemove = async () => {
    if (!canRemove) return;
    const removedName = items[selectedIndex].name;
    try {
      let removed: boolean;
      if (isTauri()) {
        const { invoke } = await import("@tauri-apps/api/core");
        removed = await invoke<boolean>("remove_tool_set", { name: removedName });
      } else {
        // Browser/webserver: delete the login's saved set on the backend.
        const resp = await fetch(`/api/remove_tool_set?name=${encodeURIComponent(removedName)}`);
        removed = resp.ok && ((await resp.json()) as boolean);
      }
      if (!removed) return; // default set / nothing removed
      await refresh();
    } catch {
      return;
    }
    onApply?.({ action: "remove", setName: removedName });
  };

  // Port of C++ `OnToolSetImportButton` -> `Ui_import_tool_set`: pick an
  // exported toolset ini and copy every set in it into the user's tool sets
  // (colliding names get " - copy" appended), then re-read the list.
  const handleImport = async () => {
    if (isTauri()) {
      try {
        const [{ invoke }, { open }] = await Promise.all([
          import("@tauri-apps/api/core"),
          import("@tauri-apps/plugin-dialog"),
        ]);
        const path = await open({
          multiple: false,
          directory: false,
          defaultPath: await pickerInitialDir(PickerKey.loadToolSet),
          filters: [{ name: getTigerResource("TIGER_TOOLSETS_FILTER"), extensions: ["ini"] }],
          title: getTigerResource("TIGER_TOOLSETS_IMPORT"),
        });
        if (typeof path !== "string" || !path) return;
        rememberPickerDir(PickerKey.loadToolSet, path);
        const imported = await invoke<string[]>("import_tool_set", { path });
        if (imported.length > 0) await refresh();
      } catch (e) {
        console.error("Import tool set failed:", e);
        return;
      }
    }
    onApply?.({ action: "import" });
  };

  // Port of C++ `OnToolSetExportButton` -> `Ui_export_tool_set`: write the
  // selected set's ini section to a standalone file. Default filename matches
  // the C++ `{site}_tiger_toolset.ini`.
  const handleExport = async () => {
    if (!hasSelection) return;
    const exportName = items[selectedIndex].name;
    if (isTauri()) {
      try {
        const [{ invoke }, { save }] = await Promise.all([
          import("@tauri-apps/api/core"),
          import("@tauri-apps/plugin-dialog"),
        ]);
        let site = "";
        try {
          site = await invoke<string>("get_site_config_name");
        } catch {
          // no site name — fall back to the bare default filename
        }
        const path = await save({
          defaultPath: await pickerSavePath(
            PickerKey.saveToolSet,
            `${site ? site + "_" : ""}tiger_toolset.ini`,
          ),
          filters: [{ name: getTigerResource("TIGER_TOOLSETS_FILTER"), extensions: ["ini"] }],
          title: getTigerResource("TIGER_TOOLSETS_EXPORT"),
        });
        if (!path) return;
        rememberPickerDir(PickerKey.saveToolSet, path);
        const ok = await invoke<boolean>("export_tool_set", { name: exportName, path });
        if (!ok) {
          console.error(`Export tool set "${exportName}" failed`);
          return;
        }
      } catch (e) {
        console.error("Export tool set failed:", e);
        return;
      }
    }
    onApply?.({ action: "export", setName: exportName });
  };

  if (!open) return null;

  const title = getTigerResource("TIGER_TOOLSETS_TITLE");

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog} role="dialog" aria-label={title}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button className="acrylic-close-btn" onClick={onClose}>
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Save Current Set */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_EXPORT_SET_SAVE")}</span>
            <div style={styles.card}>
              <div style={styles.fieldRow}>
                <input
                  style={styles.input}
                  type="text"
                  value={setName}
                  onChange={(e) => {
                    setSetName(e.target.value);
                    setSaveError("");
                  }}
                />
                <button
                  className="btn-acrylic-primary"
                  onClick={() => void handleSave()}
                  disabled={!setName.trim()}
                >
                  {getTigerResource("TIGER_RES_SAVE")}
                </button>
              </div>
              {saveError && (
                <div style={{ fontSize: 12, color: "#b91c1c" }}>{saveError}</div>
              )}
            </div>
          </div>

          {/* Tool Set Windows */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_TOOLSETS_WIN")}</span>
            <div style={styles.card}>
              <div style={styles.btnRow}>
                <button
                  className="btn-acrylic-secondary"
                  onClick={handleCloseAll}
                  disabled={!canCloseAll}
                >
                  {getTigerResource("TIGER_RES_CLOSE_ALL")}
                </button>
              </div>
            </div>
          </div>

          {/* Load/Remove Selected Set */}
          <div style={{ ...styles.section, flex: 1, minHeight: 0 }}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_EXPORT_SET_LOAD")}</span>
            <div style={styles.btnRow}>
              <button
                className="btn-acrylic-secondary"
                onClick={() => {
                  if (hasSelection) void handleLoad(items[selectedIndex].name);
                }}
                disabled={!hasSelection}
              >
                {getTigerResource("TIGER_RES_LOAD")}
              </button>
              <button
                className="btn-acrylic-secondary"
                onClick={handleRemove}
                disabled={!canRemove}
              >
                {getTigerResource("TIGER_RES_REMOVE")}
              </button>
              <button className="btn-acrylic-secondary" onClick={() => void handleImport()}>
                {getTigerResource("TIGER_TOOLSETS_IMPORT")}
              </button>
              <button
                className="btn-acrylic-secondary"
                onClick={() => void handleExport()}
                disabled={!hasSelection}
              >
                {getTigerResource("TIGER_TOOLSETS_EXPORT")}
              </button>
            </div>
            <div style={{ ...styles.tableWrap, flex: 1, minHeight: 120, display: "flex" }}>
              <div className="list-control" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>{getTigerResource("TIGER_EXPORT_CHANNEL_NAME")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr
                        key={idx}
                        className={selectedIndex === idx ? "selected" : ""}
                        onClick={() => setSelectedIndex(idx)}
                        onDoubleClick={() => {
                          setSelectedIndex(idx);
                          void handleLoad(item.name);
                        }}
                      >
                        <td>{item.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" onClick={onClose}>
            {getTigerResource("TIGER_RES_CLOSE")}
          </button>
        </div>
      </div>
    </div>
  );
}
